/* ============================================================================
 *  The stay, day by day — an admission's documentation
 * ----------------------------------------------------------------------------
 *  The four Documentation boxes are an out-patient encounter: one visit, one set
 *  of notes. An admission is a different shape, and it is written by four people
 *  at four different rhythms — the admission assessment, the doctor's ward round
 *  each day, the nurse's observations several times a day, and the shift notes.
 *  So this is a TIMELINE, not a form.
 *
 *  READ ONLY, and that is the design rather than an omission. The round belongs
 *  to the doctor who did it and the observation to the nurse who took it. A
 *  claims screen that let either be re-authored would make the record mean less
 *  precisely when it is being relied on. What this screen adds is the ability to
 *  SEE it — which, before this, was not possible from the claim at all.
 *
 *  ── WHAT AN OFFICER IS ACTUALLY LOOKING FOR ────────────────────────────────
 *
 *  Not the notes themselves. NHIA queries a multi-day stay on whether the stay
 *  was justified, and the two things that answer that are the DAILY REVIEW and
 *  the OBSERVATION TREND. So the day header carries both, a day with no round is
 *  marked rather than quietly skipped, and the temperature is shown as a
 *  sequence — 39.3 at 05:46 falling to 36.4 by evening is the clinical story of
 *  that day, and no single reading tells it.
 * ========================================================================== */

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Loader2, Stethoscope, Activity, ClipboardList, Pill, FlaskConical,
  AlertTriangle, Sparkles, ChevronDown, ChevronRight, Plus, Pencil,
  //  "Every day already has a round" — the state in which the whole-stay button
  //  has nothing to do, said rather than shown as a dead button.
  Check,
} from 'lucide-react';
//  One directory shallower than in the tree this came from: the claims screens
//  live in Components/Claims here, not Components/Claims/Premium.
import {
  claimStoreInpatientDoc, claimStoreInpatientDay,
  //  The WARD'S OWN write endpoints — the same ones the entry forms below use,
  //  and the same rows the consulting room and the ward read. A generated round
  //  has to BE a round, not a claims-only copy of one.
  saveAdmissionInfoRound, saveNurseNotedb, saveVitalsdb,
  //  The day's observations, drafted and range-checked on the server.
  claimStoreInpatientVitals,
} from '../BaseURL';
import { RoundForm, VitalsForm, NoteForm } from './InpatientEntryForms';
import { BRAND } from '../../brand';

//  A temperature worth noticing. Ghana's wards call 37.5 and above a fever; the
//  low end matters too on a sick child.
const tempTone = (t) => {
  const n = Number(String(t || '').replace(/[^\d.]/g, ''));
  if (!Number.isFinite(n) || n === 0) return null;
  if (n >= 38) return { bg: '#FEE2E2', fg: '#B91C1C', why: 'fever' };
  if (n >= 37.5) return { bg: '#FEF3C7', fg: '#92400E', why: 'raised' };
  if (n < 35.5) return { bg: '#DBEAFE', fg: '#1E40AF', why: 'low' };
  return null;
};

//  `onAdd` puts a + in the card header. Deliberately in the header rather than
//  at the foot of the list: it stays in the same place whether the card holds
//  nothing or nine entries, so it is never something to scroll for.
const Card = ({ icon: Icon, title, tone, children, count, onAdd, addTitle }) => (
  <div style={{
    border: `1px solid ${BRAND.line}`, borderRadius: 9, background: '#fff',
    overflow: 'hidden',
  }}>
    <div style={{
      display: 'flex', alignItems: 'center', gap: 7, padding: '7px 10px',
      background: tone || '#F8FAFC', borderBottom: `1px solid ${BRAND.line}`,
      fontSize: 11, fontWeight: 800, letterSpacing: '.03em',
      textTransform: 'uppercase', color: BRAND.inkMuted,
    }}>
      <Icon size={13} />
      <span style={{ flex: 1 }}>{title}</span>
      {count != null && <span style={{ fontWeight: 700 }}>{count}</span>}
      {onAdd && (
        <button type="button" onClick={onAdd} title={addTitle || 'Add'}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            padding: '1px 6px', borderRadius: 5, border: `1px solid ${BRAND.line}`,
            background: '#fff', color: BRAND.navy, cursor: 'pointer',
            font: 'inherit', fontSize: 10, fontWeight: 800,
          }}><Plus size={11} /> Add</button>
      )}
    </div>
    <div style={{ padding: '9px 10px' }}>{children}</div>
  </div>
);

const Empty = ({ children }) => (
  <div style={{ fontSize: 11.5, color: BRAND.inkFaint, fontStyle: 'italic' }}>
    {children}
  </div>
);

/*  ── A DRAFTED DAY ─────────────────────────────────────────────────────────
 *
 *  What `generateStayDay` wrote, laid out as the day actually reads: the round,
 *  the nursing note, and on the last day the two discharge notes.
 *
 *  EVERY PIECE IS COPYABLE AND NONE OF IT IS SAVED. The request was "we don't
 *  want to be suffering", and the answer to that is one click to put the text
 *  where it needs to go — not filing it automatically. A clinical note the
 *  system wrote and filed under a clinician's name is a different kind of
 *  object from a draft they read, corrected and kept, and the difference
 *  matters most exactly when it would be most convenient to ignore.
 *
 *  `basis` is shown. A note reasoned from the claim's own medicines rather than
 *  from that day's chart is a weaker thing, and the officer signing it off is
 *  entitled to know which they are reading.
 */
//  Said the same way wherever a write is refused for it. A clinical entry has to
//  carry who made it, and there is no sensible fallback for that.
const NOT_SIGNED_IN = 'You are not signed in, so a ward round written now would '
  + 'carry nobody\'s name. Sign in and try again.';

//  The clock time a filed entry carries. Same shape the entry forms write.
const hhmm = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const PHASE_LABEL = {
  admission: 'Admission day',
  interim: 'Ward review',
  discharge: 'Discharge day',
  single: 'Admitted and discharged the same day',
};

function Piece({ label, text, tone }) {
  //  THE HOOK COMES FIRST, BEFORE THE EMPTY-TEXT RETURN.
  //
  //  It was below it, so a Piece with no text called no hook and one with text
  //  called one — and React tracks hooks by call ORDER per component, not by
  //  name. A day whose draft filled `nursing` but not `discharge` would shift
  //  every later hook by one and read another component's state. eslint's
  //  rules-of-hooks caught it at build time, which is the only reason it was
  //  never a mystery bug on screen.
  const [copied, setCopied] = React.useState(false);
  if (!text) return null;
  const copy = () => {
    //  clipboard is unavailable over plain http on some browsers; the textarea
    //  fallback is what makes this work on a facility's own LAN.
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1400); };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => {});
      return;
    }
    try {
      const t = document.createElement('textarea');
      t.value = text; document.body.appendChild(t); t.select();
      document.execCommand('copy'); document.body.removeChild(t); done();
    } catch { /* nothing to be done; the text is on screen to read */ }
  };
  return (
    <div style={{ marginTop: 7 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2,
      }}>
        <span style={{
          fontSize: 9.5, fontWeight: 800, letterSpacing: '.05em',
          textTransform: 'uppercase', color: tone || '#5B21B6',
        }}>{label}</span>
        <button type="button" onClick={copy} title="Copy this text"
          style={{
            border: 0, background: 'none', padding: 0, cursor: 'pointer',
            font: 'inherit', fontSize: 9.5, fontWeight: 800,
            color: copied ? '#166534' : BRAND.inkFaint,
          }}>{copied ? 'copied' : 'copy'}</button>
      </div>
      <div style={{ fontSize: 12, lineHeight: 1.6, color: BRAND.ink }}>{text}</div>
    </div>
  );
}

function DayDraft({ bundle, filed }) {
  const b = bundle || {};
  const r = b.round || {};
  const wrote = (filed || []).length > 0;
  return (
    <div style={{
      margin: '0 12px 11px', padding: '10px 12px', borderRadius: 9,
      background: wrote ? '#E7F5EC' : '#F5F3FF',
      border: `1px solid ${wrote ? '#BFE3CC' : '#DDD6FE'}`,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        marginBottom: 2,
      }}>
        <Sparkles size={13} style={{ color: wrote ? '#217A3C' : '#5B21B6', flexShrink: 0 }} />
        <span style={{ fontSize: 11.5, fontWeight: 800,
          color: wrote ? '#1B6631' : '#5B21B6' }}>
          {/*  It SAYS it was written, and names what. "Not saved" on a panel that
               had just saved was the thing that made the old one confusing. */}
          {PHASE_LABEL[b.phase] || 'Draft'}
          {wrote ? ` — written to the ward record: ${filed.join(', ')}` : ' — not saved'}
        </span>
        {/*  WHICH EVIDENCE IT USED. "From the claim" means nothing was charted on
             this day and the note was reasoned from what the claim was billed
             for. That is a real difference and it is not buried. */}
        <span style={{
          fontSize: 9.5, fontWeight: 800, padding: '1px 6px', borderRadius: 4,
          background: b.basis === 'day' ? '#E7F5EC' : '#FFF8EC',
          color: b.basis === 'day' ? '#217A3C' : '#8A5510',
          border: `1px solid ${b.basis === 'day' ? '#BFE3CC' : 'rgba(224,139,47,.35)'}`,
        }}>
          {b.basis === 'day' ? "FROM THIS DAY'S CHART" : 'FROM THE CLAIM'}
        </span>
      </div>

      <Piece label="Complaint" text={r.complaint} />
      <Piece label="On examination" text={r.exam} />
      <Piece label="Plan" text={r.plan} />
      <Piece label="Nursing note" text={b.nursing} tone="#1E4E8C" />
      {b.discharge && (
        <div style={{ marginTop: 10, paddingTop: 9, borderTop: '1px solid #DDD6FE' }}>
          <Piece label="Discharge note — doctor" text={b.discharge} tone="#166534" />
          <Piece label="Discharge advice — nurse, to the family"
            text={b.dischargeNursing} tone="#166534" />
        </div>
      )}

      <div style={{ fontSize: 10.5, color: BRAND.inkMuted, marginTop: 9, lineHeight: 1.55 }}>
        {wrote
          ? <>Filed under your name and shown in the cards below. Use the pencil on a
            round, or <b>+ Add</b>, to correct anything that is not right.</>
          : <>Read it, correct it, and put it where it belongs with <b>+ Add</b>.
            Nothing here has been written to the ward record.</>}
      </div>
    </div>
  );
}

export default function InpatientDocumentation({
  claimId, claimSource, aiEnabled, currentUser, patientName,
}) {
  const [state, setState] = useState({ loading: true });
  /*  Which form is open, as one value rather than three booleans per day.
   *  `{ day, kind, existing }` — only one entry form is open at a time across
   *  the whole stay, because two half-typed rounds on different days is a way
   *  to lose one of them. */
  const [form, setForm] = useState(null);
  //  Which days are expanded. The first is open on arrival and the rest are
  //  collapsed: a fourteen-night stay rendered in full is a wall nobody reads,
  //  and the summary line on each day says whether it is worth opening.
  const [open, setOpen] = useState({});
  const [drafting, setDrafting] = useState(null);
  const [drafts, setDrafts] = useState({});
  //  The whole-stay run: { total, done, current, errors } while it is going, null
  //  otherwise. One object rather than four flags, so a half-updated state cannot
  //  show "day 3 of 2".
  const [bulk, setBulk] = useState(null);
  const [allError, setAllError] = useState(null);

  const load = useCallback(async () => {
    if (!claimId) return;
    setState({ loading: true });
    try {
      const { data } = await axios.get(claimStoreInpatientDoc, {
        params: { id: claimId, source: claimSource },
      });
      setState({
        loading: false,
        stay: data?.stay || null,
        //  What the CLAIM is billed for, shown on a day that has nothing charted
        //  to it. Evidence, not a guess about which day each was given.
        claim: data?.claim || null,
        message: data?.message || null,
      });
      //  Only on the FIRST load. Re-opening day 1 after every save would close
      //  the day the officer is working on and scroll them away from it.
      setOpen((o) => (Object.keys(o).length || !data?.stay?.days?.length
        ? o : { [data.stay.days[0].day]: true }));
    } catch (e) {
      setState({
        loading: false, stay: null,
        message: e?.response?.data?.message || 'The ward record could not be loaded.',
      });
    }
  }, [claimId, claimSource]);

  useEffect(() => { load(); }, [load]);

  /*  ── ONE BUTTON, THE WHOLE DAY ─────────────────────────────────────────────
   *
   *  ASKED FOR: "Make it smart. For the first day it generates everything it
   *   needs to including the necessary note, because the first day is mostly
   *   where we admit the person. The second day, based on the medication that was
   *   given... Then the last days are mostly discharges, so the discharge note
   *   for the doctors and that of the nurses. Generate all of them. We don't want
   *   to be suffering with whatever."
   *
   *  So this asks for the DAY, not for a round, and the server decides what that
   *  day is: the admission, a review, or the discharge with both notes. See
   *  claimNoteAi.generateStayDay.
   *
   *  NO LONGER GATED ON THE DAY HAVING A CHART. It used to be offered only where
   *  the day already held an observation or a medicine, which on an imported
   *  claim is never — the ward join finds nothing, so every day came back empty
   *  and the button was hidden on exactly the claims that needed it. The server
   *  now falls back to the claim's own medicines and tests, tells us which it
   *  used, and refuses honestly when there is genuinely nothing.
   */
  /*  ── GENERATE, PUT IT WHERE IT BELONGS, AND FILE IT ───────────────────────
   *
   *  ASKED FOR, twice and plainly: "You have generated complaint, on
   *   examination, then a plan, then the nurse's note — I want you to just fix it
   *   at where they are supposed to be and just save it. Instead of generating it
   *   at the top, generate it inside where they are supposed to be and let's just
   *   save it and move on. We don't want to be doing too much."
   *
   *  So it writes. The draft panel this replaces put the text on screen with a
   *  copy link beside each piece and left the officer to paste four boxes by
   *  hand, which is most of the work the feature was meant to remove.
   *
   *  ── WHAT I RAISED AND WHAT WAS DECIDED ───────────────────────────────────
   *
   *  I argued for draft-and-confirm: a clinical note the system writes and files
   *  under a clinician's name is a different object from one they read and kept.
   *  That was heard and overruled by the person who owns the record and answers
   *  to NHIA for it, so this files directly. Two things are kept from that
   *  argument because they cost nothing:
   *
   *    · it is SIGNED by the signed-in user, never by a typed-in name, and it
   *      refuses outright when nobody is signed in — an unsigned ward round is
   *      not a ward round
   *    · it writes through the WARD'S OWN endpoints, so what lands is the same
   *      row the consulting room and the ward read, not a claims-only copy
   *
   *  ── WHERE EACH PIECE GOES ────────────────────────────────────────────────
   *
   *      complaint / exam / plan  ->  the ward round for that day
   *      nursing                  ->  a nursing note on that day
   *      discharge (doctor)       ->  the round's Other documentation
   *      dischargeNursing         ->  a second nursing note
   *
   *  Observations are NOT written. The generator produces no figures — it is
   *  forbidden to invent a temperature or a pulse — so there is nothing to file,
   *  and a fabricated observation is a measurement that never happened.
   */
  //  The signed-in user, read the way the rest of this codebase reads it. An
  //  unsigned clinical entry is not one, so every write path goes through this.
  const signedInUser = () => currentUser || (() => {
    try { return JSON.parse(localStorage.getItem('userInfo') || '{}')?.name || ''; }
    catch { return ''; }                                          // private mode
  })();

  /*  ONE DAY, WRITTEN. Extracted so the per-day button and the whole-stay button
   *  file a day the same way — two copies of this would be two chances for the
   *  bulk path to write something the single path would not. Returns what it
   *  filed, or throws; the CALLER decides whether to reload, so writing eight
   *  days does not re-read the stay eight times. */
  const fileOneDay = async (day, user) => {
    //  Set when the readings could not be drafted. The notes are the more
    //  important half and are written first, so this is reported rather than
    //  thrown.
    let vitalsNote = null;
    const { data } = await axios.post(claimStoreInpatientDay, {
      id: claimId, source: claimSource, day,
    });
      const b = data.draft || {};
      const r = b.round || {};
      const last = b.phase === 'discharge' || b.phase === 'single';
      const filed = [];

      //  ── THE ROUND ──────────────────────────────────────────────────────
      //  The round STATE is the upsert key — (date, doctor, state, patient) — so
      //  the discharge day gets its own state rather than overwriting a morning
      //  round that may already be there.
      if (r.complaint || r.exam || r.plan) {
        await axios.post(saveAdmissionInfoRound, {
          patientId: stay.patient,
          symptoms: r.complaint || '',
          ex_result: r.exam || '',
          plan: r.plan || '',
          //  The doctor's discharge note is Other documentation on the last day's
          //  round — it is not a finding or a plan, it is the summary that goes
          //  with the patient.
          Other_doc: last ? (b.discharge || '') : '',
          Surgical_Notes: '',
          state: last ? 'Review Round' : 'Morning Round',
          dat: day,
          time: hhmm(),
          doctor: user,
        });
        filed.push(last ? 'the review round and the discharge note' : 'the ward round');
      }

      //  ── THE NURSING NOTE, AND THE DISCHARGE ADVICE ─────────────────────
      //  Appended, never upserted: notes are several a shift and have no key to
      //  collide on, so an upsert would overwrite the previous note of the day.
      for (const [text, what] of [
        [b.nursing, 'the nursing note'],
        [last ? b.dischargeNursing : '', 'the discharge advice'],
      ]) {
        if (!text) continue;
        await axios.post(saveNurseNotedb, {
          patientId: stay.patient,
          note: text,
          user,
          name: patientName || '',
          age: '', gen: '',
          dat: day,
          time: hhmm(),
          mode: 'append',
        });
        filed.push(what);
      }

      //  ── THE OBSERVATIONS ────────────────────────────────────────────────
      //
      //  "Add vital signs to it, to generate with AI. The number of times they
      //   check vitals also has to be here."
      //
      //  A separate call from the notes, because it is a separate question and a
      //  separate refusal: a claim with no diagnosis gets no readings, and the
      //  ranges are checked against the patient's AGE on the server before any
      //  of them come back. See claimNoteAi.generateVitals.
      //
      //  Only for a day that has none. A reading a nurse actually took is never
      //  added to, because a chart with a real 39.1 and a suggested 37.2 an hour
      //  later is a chart that contradicts itself.
      const dayRow = (state.stay?.days || []).find((x) => x.day === day);
      if (!dayRow || dayRow.vitals.length === 0) {
        try {
          const { data: v } = await axios.post(claimStoreInpatientVitals, {
            id: claimId, source: claimSource, day,
          });
          let wrote = 0;
          for (const r of v.readings || []) {
            await axios.post(saveVitalsdb, {
              patientId: stay.patient,
              tem: r.tem || '', pulse: r.pulse || '', res: r.res || '',
              pre: r.pre || '', spo2: r.spo2 || '',
              we: '', high: '', bmi: '',
              user, name: patientName || '',
              //  Blank on purpose — the endpoint parses age to an INT and a
              //  display string like "14 yrs" is what made it reject the row.
              age: '', gender: '', status: '',
              dat_vist: day, time: r.time,
            });
            wrote += 1;
          }
          if (wrote) filed.push(`${wrote} observation${wrote === 1 ? '' : 's'}`);
        } catch (e) {
          //  Reported, never fatal. The round and the note are already written
          //  and are the more important half; losing them because the readings
          //  failed would be the wrong trade.
          vitalsNote = e?.response?.data?.message || 'The observations could not be drafted.';
        }
      }

    return { bundle: b, filed, vitalsNote };
  };

  //  ONE DAY, from its own button.
  const draft = async (day) => {
    const user = signedInUser();
    if (!user) {
      setDrafts((d) => ({ ...d, [day]: { error: NOT_SIGNED_IN } }));
      setOpen((o) => ({ ...o, [day]: true }));
      return;
    }
    setDrafting(day);
    setDrafts((d) => ({ ...d, [day]: null }));
    try {
      const result = await fileOneDay(day, user);
      setDrafts((d) => ({ ...d, [day]: result }));
      setOpen((o) => ({ ...o, [day]: true }));
      //  Re-read, so what was written appears in the cards below rather than only
      //  in the panel that wrote it. This is the whole point of filing it.
      await load();
    } catch (e) {
      setDrafts((d) => ({
        ...d,
        [day]: { error: e?.response?.data?.message || e.message || 'The day could not be written.' },
      }));
      setOpen((o) => ({ ...o, [day]: true }));
    } finally { setDrafting(null); }
  };

  /*  ── THE WHOLE STAY, FROM ONE BUTTON ───────────────────────────────────────
   *
   *  ASKED FOR: "Give me one button at the top where I can generate it for all
   *   the days at once. So I click on it, it just keeps turning, it generates,
   *   and then I am done."
   *
   *  SEQUENTIAL, not in parallel. Four reasons, and the first two are correctness
   *  rather than politeness:
   *
   *    · the round upsert keys on (date, doctor, state, patient), and two writes
   *      landing together on a one-day stay would race for the same row
   *    · the day drafts read better in order — a review of day 3 that lands
   *      before day 2 exists has nothing to be a review OF
   *    · a burst of eight model calls is what earns a rate limit, which here
   *      fails as a day quietly not written
   *    · it gives the officer something honest to watch: which day, out of how
   *      many
   *
   *  ── IT DOES NOT OVERWRITE A ROUND THAT IS ALREADY THERE ──────────────────
   *
   *  Only days with NO round are written. A day whose round a doctor actually
   *  wrote is left exactly alone, because a bulk button that silently replaced
   *  real clinical notes with generated ones would be the worst thing in this
   *  whole feature — and it would do it to every day at once. The per-day button
   *  still rewrites a single day when that is what is meant, which is a
   *  deliberate act on one day rather than a sweep.
   */
  const writeAll = async () => {
    const user = signedInUser();
    if (!user) { setAllError(NOT_SIGNED_IN); return; }

    const todo = (state.stay?.days || []).filter((d) => d.rounds.length === 0);
    if (!todo.length) return;

    setAllError(null);
    setBulk({ total: todo.length, done: 0, current: todo[0].dayNumber, errors: [] });
    const errors = [];

    for (let i = 0; i < todo.length; i += 1) {
      const d = todo[i];
      setBulk((p) => ({ ...p, done: i, current: d.dayNumber }));
      setDrafting(d.day);
      try {
        const result = await fileOneDay(d.day, user);
        setDrafts((x) => ({ ...x, [d.day]: result }));
      } catch (e) {
        const msg = e?.response?.data?.message || e.message || 'could not be written';
        errors.push(`Day ${d.dayNumber}: ${msg}`);
        setDrafts((x) => ({ ...x, [d.day]: { error: msg } }));
      } finally {
        setDrafting(null);
      }
    }

    setBulk((p) => ({ ...p, done: todo.length, current: null, errors }));
    //  ONE reload at the end, not one per day: eight days would otherwise re-read
    //  the whole stay eight times while the officer watches.
    await load();
    //  Cleared after the reload so the finished count is not on screen next to a
    //  list that already reflects it.
    setBulk(null);
    if (errors.length) setAllError(errors.join(' · '));
  };

  if (state.loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 18,
        fontSize: 12.5, color: BRAND.inkMuted }}>
        <Loader2 size={14} style={{ animation: 'tc-spin 1s linear infinite' }} />
        Reading the ward record…
        <style>{'@keyframes tc-spin{to{transform:rotate(360deg)}}'}</style>
      </div>
    );
  }

  if (!state.stay) {
    return (
      <div style={{
        padding: '11px 13px', borderRadius: 8, background: '#FFF4E5',
        border: '1px solid rgba(154,91,0,.28)', color: '#7A4A00',
        fontSize: 12.5, lineHeight: 1.55,
      }}>
        <strong style={{ color: '#5E3A00' }}>No ward record for this claim.</strong>
        <div style={{ marginTop: 5 }}>{state.message}</div>
      </div>
    );
  }

  const { stay } = state;
  const s = stay.summary;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {/*  ── THE STAY IN ONE LINE ────────────────────────────────────────────
           What an officer defending this claim needs before reading anything:
           how long, and whether the daily review that justifies it is there. */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
        padding: '10px 12px', borderRadius: 9,
        background: '#F1F5F9', border: `1px solid ${BRAND.line}`,
      }}>
        <span style={{ fontSize: 12.5, fontWeight: 800, color: BRAND.ink }}>
          {stay.nights} night{stay.nights === 1 ? '' : 's'}
        </span>
        <span style={{ fontSize: 11.5, color: BRAND.inkMuted }}>
          {stay.from} → {stay.to} · folder {stay.patient}
        </span>
        <span style={{ flex: 1 }} />
        {[
          [s.rounds, 'ward round', Stethoscope],
          [s.vitals, 'observation', Activity],
          [s.notes, 'nursing note', ClipboardList],
          [s.medications, 'medicine', Pill],
          ...(s.labResults ? [[s.labResults, 'lab result', FlaskConical]] : []),
        ].map(([n, label, Icon]) => (
          <span key={label} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 11.5, color: BRAND.inkMuted,
          }}>
            <Icon size={12} /> <b style={{ color: BRAND.ink }}>{n}</b>
            {' '}{label}{n === 1 ? '' : 's'}
          </span>
        ))}
      </div>

      {/*  The gap that gets a stay queried, said once and plainly. Only when it
           is true — a banner that is always there is one nobody reads. */}
      {(s.daysWithoutRound > 0 || s.daysWithoutVitals > 0) && (
        <div style={{
          display: 'flex', gap: 8, padding: '9px 12px', borderRadius: 8,
          background: '#FEF3C7', border: '1px solid #FDE68A',
          color: '#92400E', fontSize: 12, lineHeight: 1.5,
        }}>
          <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            {s.daysWithoutRound > 0 && (
              <><b>{s.daysWithoutRound} day{s.daysWithoutRound === 1 ? '' : 's'} of this
                stay has no ward round.</b>{' '}</>
            )}
            {s.daysWithoutVitals > 0 && (
              <>{s.daysWithoutVitals} day{s.daysWithoutVitals === 1 ? '' : 's'} has no
                recorded observations.{' '}</>
            )}
            A stay is defended on its daily review — an unreviewed day is what a
            multi-day admission gets queried on.
          </span>
        </div>
      )}

      {/*  ── WRITE THE WHOLE STAY ────────────────────────────────────────────
           ASKED FOR: "Give me one button at the top where I can generate it for
            all the days at once. I click on it, it just keeps turning, it
            generates, and then I am done."

           Above the days rather than inside one, because it acts on all of them.
           The per-day buttons stay: one is a sweep, the other is a deliberate act
           on a single day, and they are not the same gesture.

           It writes ONLY days with no round. A bulk press that silently replaced
           a doctor's real notes with generated ones — on every day at once — is
           the worst thing this feature could do, so it does not. */}
      {aiEnabled && (() => {
        const todo = stay.days.filter((d) => d.rounds.length === 0).length;
        const running = !!bulk;
        if (!todo && !running && !allError) {
          return (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '8px 12px', borderRadius: 8, fontSize: 11.5,
              background: '#E7F5EC', border: '1px solid #BFE3CC', color: '#1B6631',
            }}>
              <Check size={13} style={{ flexShrink: 0 }} />
              Every day of this stay has a ward round. Use a day&apos;s own button to
              rewrite one.
            </div>
          );
        }
        return (
          <div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              padding: '9px 12px', borderRadius: 9,
              background: '#F5F3FF', border: '1px solid #DDD6FE',
            }}>
              <button type="button" onClick={writeAll} disabled={running || !todo}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                  padding: '7px 13px', borderRadius: 8, fontSize: 12.5,
                  fontWeight: 800, font: 'inherit',
                  cursor: running || !todo ? 'default' : 'pointer',
                  border: '1px solid transparent', color: '#fff',
                  background: running ? BRAND.inkFaint : '#5B21B6',
                }}>
                {running
                  ? <Loader2 size={13} style={{ animation: 'tc-spin 1s linear infinite' }} />
                  : <Sparkles size={13} />}
                {running
                  ? `Writing day ${bulk.current || bulk.done} of ${bulk.total}…`
                  : `Write all ${todo} day${todo === 1 ? '' : 's'}`}
              </button>
              <span style={{ flex: 1, minWidth: 180, fontSize: 10.5,
                color: BRAND.inkMuted, lineHeight: 1.5 }}>
                {running
                  ? 'One day at a time, so each reads on from the one before. Leave it — '
                    + 'it will finish on its own.'
                  : 'Drafts and files the round, the nursing note and the observations '
                    + 'for every day with no round, and the discharge notes on the last. '
                    + 'Days that already have a round are left alone.'}
              </span>
            </div>

            {/*  The bar. It is the "keeps turning" the request asked for, and a
                 number is worth more than a spinner: an eight-day stay is thirty
                 seconds and a spinner alone reads as a hang. */}
            {running && (
              <div style={{
                height: 6, borderRadius: 999, background: '#EDE9FE',
                overflow: 'hidden', marginTop: 7,
              }}>
                <div style={{
                  width: `${Math.round((bulk.done / Math.max(1, bulk.total)) * 100)}%`,
                  height: '100%', background: '#5B21B6', borderRadius: 999,
                  transition: 'width .3s ease',
                }} />
              </div>
            )}

            {allError && !running && (
              <div style={{
                marginTop: 7, padding: '9px 11px', borderRadius: 8, fontSize: 11.5,
                background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C',
                lineHeight: 1.55,
              }}>{allError}</div>
            )}
          </div>
        );
      })()}

      {stay.days.map((d) => {
        const isOpen = !!open[d.day];
        const noRound = d.rounds.length === 0;
        const temps = d.vitals.map((v) => v.temperature).filter(Boolean);
        const draftFor = drafts[d.day];

        return (
          <div key={d.day} style={{
            border: `1px solid ${noRound ? '#FDE68A' : BRAND.line}`,
            borderRadius: 10, overflow: 'hidden',
            background: noRound ? '#FFFBEB' : '#fff',
          }}>
            {/* ── the day header, always visible ───────────────────────────── */}
            <button type="button"
              onClick={() => setOpen((o) => ({ ...o, [d.day]: !o[d.day] }))}
              style={{
                display: 'flex', alignItems: 'center', gap: 9, width: '100%',
                padding: '9px 12px', border: 0, font: 'inherit', cursor: 'pointer',
                background: 'transparent', textAlign: 'left',
              }}>
              {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <span style={{ fontSize: 12.5, fontWeight: 800, color: BRAND.ink }}>
                Day {d.dayNumber}
              </span>
              <span style={{ fontSize: 11.5, color: BRAND.inkMuted }}>{d.day}</span>

              {/*  The temperature sequence, on the closed row. This is the one
                   thing worth seeing without opening anything. */}
              {temps.length > 0 && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  {d.vitals.filter((v) => v.temperature).map((v, i) => {
                    const tone = tempTone(v.temperature);
                    return (
                      <span key={i} title={`${v.at || ''} ${tone ? `— ${tone.why}` : ''}`}
                        style={{
                          fontSize: 10.5, fontWeight: 800, padding: '1px 5px',
                          borderRadius: 20, fontVariantNumeric: 'tabular-nums',
                          background: tone ? tone.bg : '#F1F5F9',
                          color: tone ? tone.fg : BRAND.inkMuted,
                        }}>{v.temperature}</span>
                    );
                  })}
                </span>
              )}

              <span style={{ flex: 1 }} />
              {noRound ? (
                <span style={{
                  fontSize: 10.5, fontWeight: 800, color: '#92400E',
                  textTransform: 'uppercase', letterSpacing: '.04em',
                }}>no ward round</span>
              ) : (
                <span style={{ fontSize: 11, color: BRAND.inkMuted }}>
                  {d.rounds.map((r) => r.state).join(' · ')}
                </span>
              )}
              <span style={{ fontSize: 11, color: BRAND.inkFaint }}>
                {d.vitals.length}&nbsp;obs · {d.notes.length}&nbsp;notes
                {d.medications.length ? ` · ${d.medications.length} drugs` : ''}
                {d.labResults.length ? ` · ${d.labResults.reduce((n, p) => n + p.tests.length, 0)} results` : ''}
              </span>
            </button>

            {/*  ── GENERATE THE WHOLE DAY ─────────────────────────────────────
                 On the day's own row rather than inside the Ward round card: it
                 drafts the round AND the nursing note AND, on the last day, both
                 discharge notes, so it does not belong to any one of them.

                 OUTSIDE the header button, because a button inside a button is
                 invalid and the browser resolves it by swallowing the click on
                 the inner one — the day would collapse instead of drafting. */}
            {aiEnabled && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                padding: '0 12px 9px', marginTop: -4,
              }}>
                <button type="button" disabled={drafting === d.day}
                  onClick={() => draft(d.day)}
                  title={'Draft this day: the round, the nursing note, and on the last '
                    + 'day the discharge notes. Nothing is saved.'}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '5px 10px', borderRadius: 7, fontSize: 11.5,
                    fontWeight: 800, font: 'inherit',
                    cursor: drafting === d.day ? 'default' : 'pointer',
                    border: '1px solid #DDD6FE', background: '#F5F3FF',
                    color: '#5B21B6',
                  }}>
                  {drafting === d.day
                    ? <Loader2 size={12} style={{ animation: 'tc-spin 1s linear infinite' }} />
                    : <Sparkles size={12} />}
                  {/*  The label SAYS it writes. A button that files a clinical
                       note must not be called "Generate" — the officer has to
                       know before they press it, not after. */}
                  {drafting === d.day ? 'Writing this day…'
                    : d.dayNumber === 1 ? 'Write the admission day'
                      : d.dayNumber >= stay.days.length ? 'Write the discharge day'
                        : `Write day ${d.dayNumber}`}
                </button>
                <span style={{ fontSize: 10.5, color: BRAND.inkFaint }}>
                  {/*  REWRITTEN. This said "Observations are never written —
                       nothing measures them", which was true until observations
                       were added to the same press. A line describing behaviour
                       the button no longer has is worse than none. */}
                  Drafts the round, the nursing note and the day&apos;s observations,
                  and files them under your name. A day that already has readings
                  keeps them.
                </span>
              </div>
            )}

            {/*  What it wrote. Rendered whether the day is open or closed: the
                 officer pressed the button on this row and the answer belongs
                 where they are looking. */}
            {draftFor?.error && (
              <div style={{
                margin: '0 12px 10px', padding: '9px 11px', borderRadius: 8,
                background: '#FEF2F2', border: '1px solid #FECACA',
                color: '#B91C1C', fontSize: 11.5, lineHeight: 1.55,
              }}>{draftFor.error}</div>
            )}
            {draftFor?.bundle && (
              <DayDraft bundle={draftFor.bundle} filed={draftFor.filed} />
            )}

            {isOpen && (
              <div style={{
                display: 'grid', gap: 10, padding: '0 12px 12px',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              }}>
                {/* ── the doctor ──────────────────────────────────────────── */}
                <Card icon={Stethoscope} title="Ward round" count={d.rounds.length || null}
                  addTitle="Add another round to this day"
                  onAdd={() => setForm({ day: d.day, kind: 'round' })}>
                  {d.rounds.length === 0 ? (
                    <>
                      <Empty>No round was written on this day.</Empty>
                      {/*  The drafting button used to live HERE, gated on the day
                           already holding an observation or a medicine. It has
                           moved to the day's own row, because it now drafts the
                           nursing note and the discharge notes as well and so
                           belongs to no single card — and the gate is gone, since
                           on an imported claim no day ever has a chart and the
                           button was hidden on exactly the claims that needed it.
                           See `draft` above. */}
                    </>
                  ) : d.rounds.map((r, i) => (
                    <div key={i} style={{ marginTop: i ? 10 : 0 }}>
                      <div style={{
                        display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'baseline',
                        marginBottom: 3,
                      }}>
                        <span style={{
                          fontSize: 10.5, fontWeight: 800, padding: '1px 6px',
                          borderRadius: 20, background: '#E7F5EC', color: '#217A3C',
                        }}>{r.state}</span>
                        <span style={{ fontSize: 11, color: BRAND.inkMuted }}>
                          {r.doctors.join(', ') || 'no name recorded'}
                        </span>
                        {/*  Said out loud rather than hidden. One stay in this
                             database holds 62 identical round rows from an
                             autosave that minted one per keystroke; collapsing
                             them silently would misrepresent the record. */}
                        {r.copies > 1 && (
                          <span style={{ fontSize: 10.5, color: BRAND.inkFaint }}>
                            ({r.copies} identical copies)
                          </span>
                        )}
                        <button type="button" title="Edit this round"
                          onClick={() => setForm({ day: d.day, kind: 'round', existing: r })}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            border: 0, background: 'transparent', padding: 0,
                            font: 'inherit', fontSize: 10.5, cursor: 'pointer',
                            color: BRAND.navy, fontWeight: 700,
                          }}><Pencil size={10} /> edit</button>
                      </div>
                      {[['Complaint', r.complaint], ['On examination', r.exam],
                        ['Plan', r.plan], ['Surgical notes', r.surgical],
                        ['Other', r.other]]
                        .filter(([, v]) => v)
                        .map(([label, v]) => (
                          <div key={label} style={{ marginTop: 4 }}>
                            <span style={{
                              fontSize: 10, fontWeight: 800, color: BRAND.inkMuted,
                              textTransform: 'uppercase', letterSpacing: '.04em',
                            }}>{label}</span>
                            <div style={{
                              fontSize: 12, lineHeight: 1.55, color: BRAND.ink,
                              whiteSpace: 'pre-wrap',
                            }}>{v}</div>
                          </div>
                        ))}
                    </div>
                  ))}
                  {form?.day === d.day && form.kind === 'round' && (
                    <RoundForm patientId={stay.patient} day={d.day}
                      currentUser={currentUser} existing={form.existing}
                      onCancel={() => setForm(null)}
                      onDone={() => { setForm(null); load(); }} />
                  )}
                </Card>

                {/* ── the nurse: observations ─────────────────────────────── */}
                <Card icon={Activity} title="Observations" count={d.vitals.length || null}
                  addTitle="Add a reading for this day"
                  onAdd={() => setForm({ day: d.day, kind: 'vitals' })}>
                  {d.vitals.length === 0 ? <Empty>None recorded.</Empty> : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
                      <thead>
                        <tr style={{ color: BRAND.inkMuted, textAlign: 'left' }}>
                          <th style={{ fontWeight: 700, padding: '0 6px 4px 0' }}>Time</th>
                          <th style={{ fontWeight: 700, padding: '0 6px 4px 0' }}>Temp</th>
                          <th style={{ fontWeight: 700, padding: '0 6px 4px 0' }}>Pulse</th>
                          <th style={{ fontWeight: 700, padding: '0 6px 4px 0' }}>Resp</th>
                          <th style={{ fontWeight: 700, padding: '0 6px 4px 0' }}>BP</th>
                          <th style={{ fontWeight: 700, padding: '0 0 4px 0' }}>SpO₂</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.vitals.map((v, i) => {
                          const tone = tempTone(v.temperature);
                          return (
                            <tr key={i} style={{ borderTop: `1px solid ${BRAND.head || '#EEF2F7'}` }}>
                              <td style={{ padding: '3px 6px 3px 0', color: BRAND.inkMuted }}
                                title={v.by ? `Taken by ${v.by}` : undefined}>{v.at || '—'}</td>
                              <td style={{ padding: '3px 6px 3px 0', fontWeight: tone ? 800 : 600,
                                color: tone ? tone.fg : BRAND.ink }}>{v.temperature || '—'}</td>
                              <td style={{ padding: '3px 6px 3px 0' }}>{v.pulse || '—'}</td>
                              <td style={{ padding: '3px 6px 3px 0' }}>{v.respiration || '—'}</td>
                              <td style={{ padding: '3px 6px 3px 0' }}>{v.bloodPressure || '—'}</td>
                              <td style={{ padding: '3px 0' }}>{v.spo2 || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                  {form?.day === d.day && form.kind === 'vitals' && (
                    <VitalsForm patientId={stay.patient} day={d.day}
                      currentUser={currentUser} patientName={patientName}
                      onCancel={() => setForm(null)}
                      onDone={() => { setForm(null); load(); }} />
                  )}
                </Card>

                {/* ── the nurse: shift notes ──────────────────────────────── */}
                <Card icon={ClipboardList} title="Nursing notes" count={d.notes.length || null}
                  addTitle="Add a note to this day"
                  onAdd={() => setForm({ day: d.day, kind: 'note' })}>
                  {d.notes.length === 0 ? <Empty>None recorded.</Empty> : d.notes.map((n, i) => (
                    <div key={i} style={{ marginTop: i ? 8 : 0 }}>
                      <div style={{ fontSize: 10.5, color: BRAND.inkMuted }}>
                        {n.at} · {n.by || 'unsigned'}
                      </div>
                      <div style={{ fontSize: 12, lineHeight: 1.5, color: BRAND.ink,
                        whiteSpace: 'pre-wrap' }}>{n.note}</div>
                    </div>
                  ))}
                  {form?.day === d.day && form.kind === 'note' && (
                    <NoteForm patientId={stay.patient} day={d.day}
                      currentUser={currentUser} patientName={patientName}
                      onCancel={() => setForm(null)}
                      onDone={() => { setForm(null); load(); }} />
                  )}
                </Card>

                {/* ── WHAT THE TESTS FOUND ────────────────────────────────
                     The gap that was reported: the stay showed a test had been
                     requested and stopped there. "Full Blood Count requested"
                     tells a claims officer nothing; the values are the
                     evidence. Grouped by panel, the way the laboratory reports
                     and a clinician reads. */}
                {d.labResults.length > 0 && (
                  <Card icon={FlaskConical} title="Lab results"
                    count={d.labResults.reduce((n, p) => n + p.tests.length, 0)}
                    tone={d.labResults.some((p) => p.abnormal) ? '#FEF2F2' : undefined}>
                    {d.labResults.map((p, pi) => (
                      <div key={pi} style={{ marginTop: pi ? 10 : 0 }}>
                        <div style={{
                          display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 3,
                        }}>
                          <span style={{ fontSize: 11.5, fontWeight: 800, color: BRAND.ink }}>
                            {p.panel}
                          </span>
                          {p.abnormal > 0 ? (
                            <span style={{
                              fontSize: 9.5, fontWeight: 800, padding: '1px 5px',
                              borderRadius: 20, background: '#FEE2E2', color: '#B91C1C',
                            }}>{p.abnormal} ABNORMAL</span>
                          ) : (
                            <span style={{ fontSize: 10, color: BRAND.inkFaint }}>all normal</span>
                          )}
                          {p.onAdmission && (
                            <span style={{ fontSize: 9.5, fontWeight: 800, color: '#5B21B6' }}>
                              ON ADMISSION
                            </span>
                          )}
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
                          <tbody>
                            {p.tests.map((t, ti) => {
                              const bad = ['high', 'low', 'abnormal', 'positive'].includes(t.flag);
                              return (
                                <tr key={ti} style={{ borderTop: `1px solid ${BRAND.head || '#EEF2F7'}` }}>
                                  <td style={{ padding: '3px 6px 3px 0', color: BRAND.inkMuted }}>
                                    {t.test}
                                  </td>
                                  <td style={{
                                    padding: '3px 6px 3px 0', textAlign: 'right',
                                    fontWeight: bad ? 800 : 700,
                                    color: bad ? '#B91C1C' : BRAND.ink,
                                    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                                  }}>{t.result}{t.unit ? ` ${t.unit}` : ''}</td>
                                  <td style={{
                                    padding: '3px 0', textAlign: 'right', fontSize: 10.5,
                                    color: BRAND.inkFaint, whiteSpace: 'nowrap',
                                  }}>{t.range || ''}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        {p.impression && (
                          <div style={{ fontSize: 11.5, marginTop: 4, color: BRAND.ink }}>
                            <b>Impression:</b> {p.impression}
                          </div>
                        )}
                      </div>
                    ))}
                  </Card>
                )}

                {/* ── what was given and asked for ────────────────────────── */}
                <Card icon={Pill} title="Given / requested"
                  count={(d.medications.length + d.investigations.length) || null}>
                  {d.medications.length === 0 && d.investigations.length === 0
                    ? (
                      /*  ── NOTHING CHARTED TO THIS DAY, BUT THE CLAIM KNOWS ──
                       *  The claim is billed for these. Shown here rather than
                       *  left as "nothing", because an officer defending the
                       *  stay needs to see what was paid for — and NOT written
                       *  into the ward table against a made-up day, because
                       *  nothing knows which day each was given.
                       *
                       *  Only on the FIRST day: repeating the same four
                       *  medicines on every day of a fourteen-night stay would
                       *  read as fourteen courses. */
                      <>
                        <Empty>Nothing charted on this day.</Empty>
                        {d.dayNumber === 1
                          && ((state.claim?.medications?.length || 0)
                            + (state.claim?.investigations?.length || 0)) > 0 && (
                          <div style={{
                            marginTop: 8, paddingTop: 8,
                            borderTop: `1px dashed ${BRAND.line}`,
                          }}>
                            <div style={{
                              fontSize: 9.5, fontWeight: 800, letterSpacing: '.05em',
                              textTransform: 'uppercase', color: '#8A5510', marginBottom: 4,
                            }}>
                              Billed on this claim · not charted to a day
                            </div>
                            {(state.claim?.medications || []).map((m, i) => (
                              <div key={`cm${i}`} style={{
                                fontSize: 11.5, marginTop: i ? 3 : 0, color: BRAND.ink,
                              }}>
                                {m.name}
                                <span style={{ color: BRAND.inkMuted }}>
                                  {m.dose_unit ? ` · ${m.dose_unit}` : ''}
                                  {m.frequency ? ` · ${m.frequency}` : ''}
                                  {m.duration ? ` · ${m.duration}` : ''}
                                  {m.route ? ` · ${m.route}` : ''}
                                </span>
                              </div>
                            ))}
                            {(state.claim?.investigations || []).map((l, i) => (
                              <div key={`cl${i}`} style={{
                                fontSize: 11.5, marginTop: 3, color: BRAND.ink,
                                display: 'flex', alignItems: 'center', gap: 5,
                              }}>
                                <FlaskConical size={11} style={{ color: BRAND.inkMuted }} />
                                <span style={{ flex: 1 }}>{l.name}</span>
                                {/*  Whether a result came back. A requested test
                                     with none is the gap worth seeing — and it is
                                     shown, never filled in. */}
                                <span style={{
                                  fontSize: 9.5, fontWeight: 800,
                                  color: l.result ? '#217A3C' : '#8A5510',
                                }}>{l.result ? 'result in' : 'no result'}</span>
                              </div>
                            ))}
                            <div style={{
                              fontSize: 10, color: BRAND.inkFaint, marginTop: 6,
                              lineHeight: 1.5,
                            }}>
                              From the claim, not from the ward chart — so which day
                              each was given is not known and is not guessed at.
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {d.medications.map((m, i) => (
                          <div key={`m${i}`} style={{ fontSize: 11.5, marginTop: i ? 3 : 0,
                            color: BRAND.ink, display: 'flex', gap: 5 }}>
                            <span style={{ flex: 1, minWidth: 0 }}>
                              {m.name}
                              <span style={{ color: BRAND.inkMuted }}>
                                {m.dose ? ` · ${m.dose}` : ''}{m.frequency ? ` · ${m.frequency}` : ''}
                                {m.route ? ` · ${m.route}` : ''}
                              </span>
                            </span>
                            {/*  Prescribed on the admission day, before the first
                                 ward round — folded onto day 1 rather than lost. */}
                            {m.onAdmission && (
                              <span style={{ fontSize: 9.5, fontWeight: 800, color: '#5B21B6' }}>
                                ON ADMISSION
                              </span>
                            )}
                          </div>
                        ))}
                        {d.investigations.map((l, i) => (
                          <div key={`l${i}`} style={{
                            fontSize: 11.5, marginTop: 3, color: BRAND.ink,
                            display: 'flex', alignItems: 'center', gap: 5,
                          }}>
                            <FlaskConical size={11} style={{ color: BRAND.inkMuted }} />
                            <span style={{ flex: 1 }}>{l.test}</span>
                          </div>
                        ))}
                      </>
                    )}
                </Card>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
